import type { SupabaseClient } from '../../lib/supabase';

/**
 * Live preview cards for a pasted Hub link, read in the viewer's own browser
 * session with their own RLS (plan-drop3-extras.md section 3, P1-P5).
 *
 * P1: only the Hub's own routes ever preview. P2: no row back reads as a
 * plain link, no "no access" text (that would confirm the record exists).
 * P3: only real columns, matched exactly to what the database has (checked
 * against database.types.ts, cited per kind below). P4: caching is the
 * caller's job (a React Query cache keyed by record, since the page stays
 * mounted); this module only knows how to fetch one record.
 */

export type PreviewKind = 'deal' | 'lead' | 'order' | 'task';

export interface ParsedHubLink {
  kind: PreviewKind;
  id: string;
}

/**
 * Matches the app's own route shapes, checked against App.tsx:
 *   /deals?deal=<id>            (DealsMonitoringView.tsx query param)
 *   /tracker?lead=<id>          (TrackerPage.tsx:417)
 *   /cg-orders/<id>             (App.tsx:107)
 *   /projects/<projectId>?task=<id>  (ProjectPage.tsx:692)
 *
 * Absolute URLs are matched only when their origin is the page's own origin
 * (P1): anything else — including a link that merely LOOKS like one of these
 * paths on a different host — stays a plain link.
 */
export function parseHubLink(href: string, origin: string): ParsedHubLink | null {
  let url: URL;
  try {
    url = href.startsWith('/') ? new URL(href, origin) : new URL(href);
  } catch {
    return null;
  }
  if (url.origin !== origin) {
    return null;
  }
  const path = url.pathname;
  const params = url.searchParams;

  if (path === '/deals') {
    const id = params.get('deal');
    return id !== null && id !== '' ? { kind: 'deal', id } : null;
  }
  if (path === '/tracker') {
    const id = params.get('lead');
    return id !== null && id !== '' ? { kind: 'lead', id } : null;
  }
  const orderMatch = /^\/cg-orders\/([^/]+)$/.exec(path);
  if (orderMatch) {
    const id = orderMatch[1];
    return id !== undefined && id !== '' ? { kind: 'order', id } : null;
  }
  const taskMatch = /^\/projects\/[^/]+$/.exec(path);
  if (taskMatch) {
    const id = params.get('task');
    return id !== null && id !== '' ? { kind: 'task', id } : null;
  }
  return null;
}

/**
 * Which links in a message open in place on the Messages page (P1,
 * kyu-20260930-1755-polish). Every link parseHubLink knows, plus a task link
 * with no project id, "/projects/?task=<id>", which is how agent-api links a
 * deal task (a task has a project or a deal, never both). parseHubLink itself
 * stays strict: a deal task has no task preview, and widening it would put a
 * raw-URL line under every such message.
 */
export function parseOpenableLink(href: string, origin: string): ParsedHubLink | null {
  const parsed = parseHubLink(href, origin);
  if (parsed !== null) {
    return parsed;
  }
  let url: URL;
  try {
    url = href.startsWith('/') ? new URL(href, origin) : new URL(href);
  } catch {
    return null;
  }
  if (url.origin !== origin || url.pathname !== '/projects/') {
    return null;
  }
  const id = url.searchParams.get('task');
  return id !== null && id !== '' ? { kind: 'task', id } : null;
}

/**
 * A plain left click opens a record in place. Any modifier (a new tab, a new
 * window, a download) or another button is left to the browser.
 */
export function opensInPlace(event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

export interface DealPreview {
  kind: 'deal';
  name: string;
  stage: string | null;
  nextActionDate: string | null;
  owner: string | null;
}

export interface LeadPreview {
  kind: 'lead';
  name: string;
  company: string;
  stage: string | null;
}

export interface OrderPreview {
  kind: 'order';
  orderNo: string;
  client: string;
  status: string;
  owner: string | null;
}

export interface TaskPreview {
  kind: 'task';
  title: string;
  status: string | null;
  assignee: string | null;
  dueDate: string | null;
  projectId: string;
}

export type RecordPreview = DealPreview | LeadPreview | OrderPreview | TaskPreview | null;

/**
 * One record, read as the signed-in person. `null` covers both "no such
 * record" and "RLS refused it" — deliberately indistinguishable (P2): the
 * caller renders the plain link either way, with no wording that would tell
 * an unauthorized viewer which case they hit.
 */
export async function fetchRecordPreview(
  client: SupabaseClient,
  link: ParsedHubLink,
): Promise<RecordPreview> {
  if (link.kind === 'deal') {
    // deals: name, stage_id, next_action_date, owner_id (database.types.ts:1193-1215).
    const { data } = await client
      .from('deals')
      .select(
        'name, next_action_date, deal_stages:stage_id (name), profiles:owner_id (display_name, email)',
      )
      .eq('id', link.id)
      .maybeSingle();
    if (data === null) {
      return null;
    }
    const row = data;
    return {
      kind: 'deal',
      name: row.name,
      stage: row.deal_stages?.name ?? null,
      nextActionDate: row.next_action_date,
      owner:
        row.profiles === null
          ? null
          : row.profiles.display_name.trim() !== ''
            ? row.profiles.display_name
            : row.profiles.email,
    };
  }

  if (link.kind === 'lead') {
    // leads: first_name, last_name, company_name, stage_id (database.types.ts:2340-2367).
    // No owner and no next-action column on leads — not invented here.
    const { data } = await client
      .from('leads')
      .select('first_name, last_name, company_name, lead_stages:stage_id (name)')
      .eq('id', link.id)
      .maybeSingle();
    if (data === null) {
      return null;
    }
    const row = data;
    return {
      kind: 'lead',
      name: `${row.first_name} ${row.last_name}`.trim(),
      company: row.company_name,
      stage: row.lead_stages?.name ?? null,
    };
  }

  if (link.kind === 'order') {
    // cg_orders: order_no, client, status, owner_id (database.types.ts:395-419).
    const { data } = await client
      .from('cg_orders')
      .select('order_no, client, status, profiles:owner_id (display_name, email)')
      .eq('id', link.id)
      .maybeSingle();
    if (data === null) {
      return null;
    }
    const row = data;
    return {
      kind: 'order',
      orderNo: row.order_no,
      client: row.client,
      status: row.status,
      owner:
        row.profiles === null
          ? null
          : row.profiles.display_name.trim() !== ''
            ? row.profiles.display_name
            : row.profiles.email,
    };
  }

  // tasks: title, status_id, assignee_id, due_date (database.types.ts:6090-6123).
  // status_id references project_statuses (20260801000100_tasks_foundation.sql:23),
  // not a task-only table.
  const { data } = await client
    .from('tasks')
    .select(
      'title, due_date, project_id, project_statuses:status_id (name), profiles:assignee_id (display_name, email)',
    )
    .eq('id', link.id)
    .maybeSingle();
  if (data === null) {
    return null;
  }
  const row = data;
  if (row.project_id === null) {
    return null;
  }
  return {
    kind: 'task',
    title: row.title,
    status: row.project_statuses?.name ?? null,
    assignee:
      row.profiles === null
        ? null
        : row.profiles.display_name.trim() !== ''
          ? row.profiles.display_name
          : row.profiles.email,
    dueDate: row.due_date,
    projectId: row.project_id,
  };
}

/** P4: up to 3 previews per message. */
export const MAX_PREVIEWS_PER_MESSAGE = 3;
